import type { IRemoteNotification } from "@suprsend/web-sdk";

/** Single SuprSend workflow slug for Soft and Hard ETA alerts */
export const ETA_ALERT_INTENT = "eta-alert";

function readIntentField(workflowDataBlob: unknown): string | undefined {
  if (workflowDataBlob == null) {
    return undefined;
  }
  if (typeof workflowDataBlob === "string") {
    const trimmed = workflowDataBlob.trim();
    if (!trimmed) {
      return undefined;
    }
    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        const intent = (parsed as { intent?: unknown }).intent;
        if (intent == null) {
          return undefined;
        }
        return String(intent).trim() || undefined;
      }
    } catch {
      return undefined;
    }
    return undefined;
  }
  if (typeof workflowDataBlob === "object" && workflowDataBlob !== null) {
    if (!("intent" in workflowDataBlob)) {
      return undefined;
    }
    const intent = (workflowDataBlob as { intent?: unknown }).intent;
    if (intent == null) {
      return undefined;
    }
    return String(intent).trim() || undefined;
  }
  return undefined;
}

function readWorkflowDataObject(
  workflowDataBlob: unknown,
): Record<string, unknown> | null {
  if (workflowDataBlob == null) {
    return null;
  }
  if (typeof workflowDataBlob === "string") {
    try {
      const parsed: unknown = JSON.parse(workflowDataBlob.trim());
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      return null;
    }
    return null;
  }
  if (typeof workflowDataBlob === "object" && !Array.isArray(workflowDataBlob)) {
    return workflowDataBlob as Record<string, unknown>;
  }
  return null;
}

function isEtaAlertIntent(intentRaw: string): boolean {
  if (!intentRaw) {
    return false;
  }
  const normalized = intentRaw.trim().toLowerCase();
  return (
    normalized === ETA_ALERT_INTENT ||
    normalized === "send_eta_alert" ||
    normalized === "eta_alert"
  );
}

function normalizeAlertType(raw: unknown): "soft" | "hard" | null {
  if (raw == null) {
    return null;
  }
  const typeRaw = String(raw).trim().toLowerCase();
  if (typeRaw === "soft" || typeRaw === "hard") {
    return typeRaw;
  }
  return null;
}

function buildPayloadFromData(
  data: Record<string, unknown>,
): EtaAlertFeedPayload | null {
  const intentRaw = data.intent != null ? String(data.intent).trim() : "";
  // Require an ETA-specific intent so generic orderId + type soft/hard cannot open the lock
  if (!intentRaw || !isEtaAlertIntent(intentRaw)) {
    return null;
  }

  const etaAlertType = normalizeAlertType(
    data.etaAlertType ?? data.alertType ?? data.type,
  );
  const orderId =
    data.orderId != null
      ? String(data.orderId).trim()
      : data.order_id != null
        ? String(data.order_id).trim()
        : "";
  const orderNo =
    data.orderNo != null
      ? String(data.orderNo).trim()
      : data.order_no != null
        ? String(data.order_no).trim()
        : undefined;
  const eta = data.eta != null ? String(data.eta).trim() : undefined;

  if (!etaAlertType) {
    return null;
  }
  if (!orderId && !orderNo) {
    return null;
  }

  return {
    intent: ETA_ALERT_INTENT,
    orderId: orderId || orderNo || "",
    orderNo: orderNo || orderId || undefined,
    eta,
    etaAlertType,
  };
}

/**
 * Fallback when SuprSend inbox only has rendered text, e.g.
 * "ETA Alert - soft alert for order 97564484081062. Current ETA: 2026-07-29T17:49"
 */
export function parseEtaAlertFromMessageText(
  text: string | undefined | null,
): EtaAlertFeedPayload | null {
  if (!text || typeof text !== "string") {
    return null;
  }
  const softOrHard = text.match(/\b(soft|hard)\s+alert\b/i);
  if (!softOrHard) {
    return null;
  }
  const orderNoMatch = text.match(/\border\s+(\d{10,})\b/i);
  if (!orderNoMatch?.[1]) {
    return null;
  }
  const etaMatch = text.match(/Current ETA:\s*([0-9T:\-]+)/i);
  const etaAlertType = softOrHard[1].toLowerCase() as "soft" | "hard";
  const orderNo = orderNoMatch[1];
  return {
    intent: ETA_ALERT_INTENT,
    // orderNo used until FE resolves Mongo id; Extend ETA path looks up by orderNo when needed
    orderId: orderNo,
    orderNo,
    eta: etaMatch?.[1],
    etaAlertType,
  };
}

function collectCandidateDataObjects(
  notification: IRemoteNotification,
): Record<string, unknown>[] {
  const candidates: Record<string, unknown>[] = [];
  const seen = new Set<unknown>();

  const push = (value: unknown) => {
    const obj = readWorkflowDataObject(value);
    if (!obj || seen.has(obj)) {
      return;
    }
    seen.add(obj);
    candidates.push(obj);
  };

  const root = notification as unknown as Record<string, unknown>;
  push(root);
  push(root.data);
  push(root.payload);

  const messageBody = notification.message;
  if (messageBody && typeof messageBody === "object") {
    const fields = messageBody as unknown as Record<string, unknown>;
    push(fields);
    push(fields.extra_data);
    push(fields.data);
    push(fields.payload);
    push(fields.actions);
  }

  return candidates;
}

/**
 * Returns the `intent` string for inbox notification sound routing, or `undefined` if missing.
 */
export function getIntentForInboxSound(
  notification: IRemoteNotification | undefined,
): string | undefined {
  const messageBody = notification?.message;
  if (messageBody == null || typeof messageBody !== "object") {
    return undefined;
  }
  const fields = messageBody as unknown as Record<string, unknown>;
  return readIntentField(fields.extra_data) ?? readIntentField(fields.data);
}

export type EtaAlertFeedPayload = {
  intent: typeof ETA_ALERT_INTENT;
  orderId: string;
  orderNo?: string;
  eta?: string;
  etaAlertType: "soft" | "hard";
};

/**
 * Parses Soft/Hard ETA alert payload from a SuprSend feed notification.
 * Single workflow `eta-alert`; Soft vs Hard comes from `etaAlertType`.
 * Falls back to inbox message text when structured extra_data is missing.
 */
export function getEtaAlertPayloadFromNotification(
  notification: IRemoteNotification | undefined,
): EtaAlertFeedPayload | null {
  if (!notification) {
    return null;
  }

  for (const data of collectCandidateDataObjects(notification)) {
    const fromData = buildPayloadFromData(data);
    if (fromData?.orderId) {
      return fromData;
    }
  }

  const messageBody = notification.message;
  if (messageBody && typeof messageBody === "object") {
    const fields = messageBody as unknown as Record<string, unknown>;
    const header =
      fields.header != null
        ? String(fields.header)
        : fields.title != null
          ? String(fields.title)
          : "";
    const text =
      fields.text != null
        ? String(fields.text)
        : fields.body != null
          ? String(fields.body)
          : "";
    const fromText = parseEtaAlertFromMessageText(`${header} ${text}`.trim());
    if (fromText) {
      return fromText;
    }
  }

  return null;
}

/** Inbox intent the backend sends the OH once it built the final estimate. */
export const FINAL_ESTIMATE_REVIEW_INTENT = "final-estimate-review";

export type FinalEstimateReviewFeedPayload = {
  orderId: string;
  orderNo: string;
  estimateNo: string;
  expiryTime?: string;
};

/**
 * Parses a final estimate review notice from a SuprSend feed notification.
 * Needs the review intent plus orderId, orderNo and estimateNo.
 */
export function getFinalEstimateReviewPayloadFromNotification(
  notification: IRemoteNotification | undefined,
): FinalEstimateReviewFeedPayload | null {
  if (!notification) {
    return null;
  }

  for (const data of collectCandidateDataObjects(notification)) {
    const intent = data.intent != null ? String(data.intent).trim() : "";
    if (intent.toLowerCase() !== FINAL_ESTIMATE_REVIEW_INTENT) {
      continue;
    }
    const orderId = data.orderId != null ? String(data.orderId).trim() : "";
    const orderNo = data.orderNo != null ? String(data.orderNo).trim() : "";
    const estimateNo =
      data.estimateNo != null ? String(data.estimateNo).trim() : "";
    const expiryTime =
      data.expiryTime != null ? String(data.expiryTime).trim() : "";
    if (orderId && orderNo && estimateNo) {
      return expiryTime
        ? { orderId, orderNo, estimateNo, expiryTime }
        : { orderId, orderNo, estimateNo };
    }
  }

  return null;
}

/** Header the backend gives the final estimate review inbox notification. */
export const FINAL_ESTIMATE_REVIEW_HEADER = "Final Estimate Ready for Review";

/**
 * True for a final estimate review notice even when the inbox message lost
 * orderId / estimateNo: `dynamic_notification` may only keep the header, the
 * body and the intent. Matches the review intent or the header.
 */
export function isFinalEstimateReviewNotification(
  notification: IRemoteNotification | undefined,
): boolean {
  if (!notification) {
    return false;
  }

  for (const data of collectCandidateDataObjects(notification)) {
    const intent = data.intent != null ? String(data.intent).trim() : "";
    if (intent.toLowerCase() === FINAL_ESTIMATE_REVIEW_INTENT) {
      return true;
    }
  }

  const messageBody = notification.message;
  if (messageBody && typeof messageBody === "object") {
    const fields = messageBody as unknown as Record<string, unknown>;
    const header = fields.header ?? fields.title;
    if (
      header != null &&
      String(header).trim().toLowerCase() ===
        FINAL_ESTIMATE_REVIEW_HEADER.toLowerCase()
    ) {
      return true;
    }
  }

  return false;
}

/**
 * `notification_intent_sounds_mapping` from mobile config: JSON string or object, intent key → sound URL.
 */
export function intentKeyToSoundUrlMap(
  configValue: unknown,
): Record<string, string> {
  if (configValue == null || configValue === "") {
    return {};
  }
  if (
    typeof configValue === "object" &&
    !Array.isArray(configValue) &&
    configValue !== null
  ) {
    return configValue as Record<string, string>;
  }
  if (typeof configValue === "string") {
    try {
      const parsed: unknown = JSON.parse(configValue);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed as Record<string, string>;
      }
    } catch {
      return {};
    }
  }
  return {};
}
